// view-truncate-ok-file: danh sách vai trò bên trái dạng mục một dòng
import { PERMISSION_ACTIONS, PERMISSION_MODULE_GROUPS } from "@apv-erp/status";
import { useState, useEffect, useMemo, Fragment } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Save,
  X,
  Edit,
  Trash2,
  ShieldCheck,
  Loader2,
  AlertCircle,
  Eye,
  Plus,
  Pencil,
  ClipboardCheck,
  CheckCircle2,
  Download,
  Upload,
  ShoppingCart,
  ClipboardList,
  Factory,
  Microscope,
  Container,
  Layers,
  Settings,
  Truck,
  Users,
  CreditCard,
  Beaker,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { trpcClient } from "@/lib/trpc";
import type { Role } from "@/shared/schemas";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useAuthStore } from "@/stores/auth.store";

// ── Permission Actions ─────────────────────────────────────────────────────
// Danh mục thao tác / module dùng chung (packages/status/permission-catalog) — ở đây chỉ gắn biểu tượng + màu
const ACTION_STYLE: Record<string, { icon: any; color: string }> = {
  view: { icon: Eye, color: "text-sky-600" },
  create: { icon: Plus, color: "text-emerald-600" },
  update: { icon: Pencil, color: "text-amber-600" },
  delete: { icon: Trash2, color: "text-rose-600" },
  review: { icon: ClipboardCheck, color: "text-violet-600" },
  approve: { icon: CheckCircle2, color: "text-teal-600" },
  print: { icon: Printer, color: "text-slate-700" },
  export: { icon: Download, color: "text-blue-600" },
  import: { icon: Upload, color: "text-orange-600" },
};
const PERM_ACTIONS = [
  { id: "all", label: "Tất cả", icon: CheckCircle2, color: "text-indigo-600" },
  ...PERMISSION_ACTIONS.map((a) => ({ ...a, ...ACTION_STYLE[a.id] })),
];

const ACTUAL_ACTIONS = PERM_ACTIONS.filter((a) => a.id !== "all");

const GROUP_ICON: Record<string, any> = {
  sales: ShoppingCart,
  planning: ClipboardCheck,
  finance: CreditCard,
  factory: Factory,
  tech: Layers,
  quality: Beaker,
  warehouse: Container,
  system: Settings,
};
const MODULE_GROUPS = PERMISSION_MODULE_GROUPS.map((g) => ({ ...g, icon: GROUP_ICON[g.iconKey] }));

// ── Helper: check/toggle permissions ───────────────────────────────────────
function hasModuleAllPerms(permissions: string[], moduleId: string) {
  return ACTUAL_ACTIONS.every((a) =>
    permissions.includes(`${moduleId}.${a.id}`),
  );
}

// ── Component ──────────────────────────────────────────────────────────────
export default function RoleDetailPage() {
  const { roleId } = useParams({ strict: false }) as { roleId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── States ──
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // ── Fetch all roles for sidebar ──
  const { data: allRolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => trpcClient.roles.list.query() as Promise<Role[]>,
  });
  const allRoles: Role[] = allRolesData || [];

  // ── Fetch selected role ──
  const { data: roleData, isLoading: roleLoading } = useQuery({
    queryKey: ["role", roleId],
    queryFn: async () =>
      trpcClient.roles.getById.query({ id: roleId }) as Promise<Role>,
  });

  const getFlatPerms = (permsObj: any): string[] => {
    if (!permsObj) return [];
    if (Array.isArray(permsObj)) {
      return permsObj.map((p: string) =>
        p.replace(/\.edit$/, ".update").replace(/\.check$/, ".review"),
      );
    }
    if (permsObj["*"]) return ["*"];

    const flat: string[] = [];
    Object.entries(permsObj).forEach(([mod, val]: [string, any]) => {
      if (val?.actions?.includes("*")) {
        ACTUAL_ACTIONS.forEach((a) => flat.push(`${mod}.${a.id}`));
      } else if (Array.isArray(val?.actions)) {
        val.actions.forEach((act: string) => {
          const normAct = act === "edit" ? "update" : act === "check" ? "review" : act;
          flat.push(`${mod}.${normAct}`);
        });
      }
    });
    return flat;
  };

  const [role, setRole] = useState<Role | undefined>(() =>
    roleData
      ? {
          ...roleData,
          permissions: getFlatPerms(roleData.permissions),
        }
      : undefined,
  );
  useEffect(() => {
    if (roleData) {
      setRole({
        ...roleData,
        permissions: getFlatPerms(roleData.permissions),
      });
    }
  }, [roleData]);

  // ── Permission helpers ──
  const hasPerm = (moduleId: string, actionId: string): boolean => {
    if (!role) return false;
    const perms = getFlatPerms(role.permissions);
    if (perms.includes("*")) return true;
    if (actionId === "all") return hasModuleAllPerms(perms, moduleId);
    return perms.includes(`${moduleId}.${actionId}`);
  };

  const togglePerm = (moduleId: string, actionId: string) => {
    if (!role || !isEditing) return;
    const currentPerms = getFlatPerms(role.permissions);
    if (currentPerms.includes("*")) {
      toast.error("Vui lòng tắt Full Access trước khi chỉnh quyền chi tiết");
      return;
    }

    let newPerms = [...currentPerms];

    if (actionId === "all") {
      // Toggle all for this module
      const allSet = hasModuleAllPerms(newPerms, moduleId);
      if (allSet) {
        newPerms = newPerms.filter((p) => !p.startsWith(`${moduleId}.`));
      } else {
        // Remove existing and add all
        newPerms = newPerms.filter((p) => !p.startsWith(`${moduleId}.`));
        ACTUAL_ACTIONS.forEach((a) => newPerms.push(`${moduleId}.${a.id}`));
      }
    } else {
      const key = `${moduleId}.${actionId}`;
      if (newPerms.includes(key)) {
        newPerms = newPerms.filter((p) => p !== key);
      } else {
        newPerms.push(key);
      }
    }

    setRole({ ...role, permissions: newPerms });
  };

  const handleFullAccessToggle = () => {
    if (!role) return;
    const currentPerms = getFlatPerms(role.permissions);
    setRole({
      ...role,
      permissions: currentPerms.includes("*") ? [] : ["*"],
    });
  };

  const handleSave = async () => {
    if (!role) return;
    setIsSaving(true);
    try {
      const flatPerms = getFlatPerms(role.permissions);
      let canonicalPerms: Record<string, any> = {};
      if (flatPerms.includes("*")) {
        canonicalPerms = { "*": true };
      } else {
        const existingObj =
          roleData?.permissions && !Array.isArray(roleData.permissions)
            ? (roleData.permissions as Record<string, any>)
            : {};
        flatPerms.forEach((p) => {
          const [mod, act] = p.split(".");
          if (!mod || !act) return;
          if (!canonicalPerms[mod]) {
            canonicalPerms[mod] = {
              actions: [],
              dataScope: existingObj[mod]?.dataScope && existingObj[mod]?.dataScope !== "NONE" ? existingObj[mod].dataScope : "ALL",
              financialScope: existingObj[mod]?.financialScope && existingObj[mod]?.financialScope !== "NONE" ? existingObj[mod].financialScope : "ALL",
            };
          }
          if (!canonicalPerms[mod].actions.includes(act)) {
            canonicalPerms[mod].actions.push(act);
          }
        });
      }

      await trpcClient.roles.update.mutate({
        id: roleId,
        data: {
          name: role.name,
          description: role.description,
          permissions: canonicalPerms,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["role", roleId] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      trpcClient.auth.me
        .query()
        .then((freshUser) => {
          if (freshUser && freshUser.id) {
            useAuthStore.getState().setUser(freshUser as any);
          }
        })
        .catch(() => {});
      toast.success("Đã lưu cấu hình phân quyền thành công.");
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi lưu");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.roles.delete.mutate({ id: roleId });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });

      navigate({ to: "/roles" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Stats
  const permCount = useMemo(() => {
    if (!role) return 0;
    const currentPerms = getFlatPerms(role.permissions);
    if (currentPerms.includes("*")) return "Full";
    return currentPerms.length;
  }, [role]);

  if (roleLoading && !role) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <p>Không tìm thấy vai trò</p>
        <Button variant="link" asChild className="mt-4">
          <Link to="/roles">Quay lại danh sách</Link>
        </Button>
      </div>
    );
  }

  const roleFlatPerms = getFlatPerms(role.permissions);

  return (
    <>
      <StandardDetailLayout
        entityType="role"
        entityId={(role as any).id || roleId}
        title={role.roleId}
        breadcrumbs={[
          { label: "Vai trò", to: "/roles" },
          { label: role.roleId },
        ]}
        status={
          <Badge
            variant="outline"
            className={cn(
              "text-[10px] px-1.5 py-0 shrink-0",
              roleFlatPerms.includes("*")
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-indigo-50 text-indigo-700 border-indigo-200",
            )}
          >
            {roleFlatPerms.includes("*") ? "ADMIN" : "CUSTOM"}
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setIsEditing(false);
                    if (roleData) {
                      setRole({
                        ...roleData,
                        permissions: getFlatPerms(roleData.permissions),
                      });
                    }
                  }}
                >
                  <X className="mr-1 h-3 w-3" /> Hủy
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Save className="mr-1 h-3 w-3" />
                  )}
                  Lưu
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-rose-600"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="mr-1 h-3 w-3" /> Xóa
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit className="mr-1 h-3 w-3" /> Chỉnh sửa
                </Button>
              </>
            )}
          </div>
        }
      >
        <div className="flex flex-col h-[calc(100vh-140px)] overflow-hidden">
          {/* ── Main Content: Sidebar + Matrix ── */}
          <div className="flex flex-1 overflow-hidden">
            {/* ── Left: Roles Sidebar ── */}
            <aside className="hidden md:flex flex-col w-[220px] lg:w-[260px] border-r border-slate-200 bg-slate-50/80 shrink-0">
              <div className="px-3 py-2 border-b border-slate-200">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Vai trò
                </p>
              </div>
              <ScrollArea className="flex-1">
                <div className="py-1">
                  {rolesLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="px-3 py-2.5">
                          <Skeleton className="h-4 w-28" />
                        </div>
                      ))
                    : allRoles.map((r) => {
                        const isActive = r.roleId === roleId;
                        const rFlatPerms = getFlatPerms(r.permissions);
                        const isFullAccess = rFlatPerms.includes("*");
                        return (
                          <Link
                            key={r.roleId}
                            to="/roles/$roleId"
                            params={{ roleId: r.roleId }}
                            className={cn(
                              "flex items-center gap-2.5 px-3 py-2.5 mx-1 rounded-md text-xs transition-all cursor-pointer",
                              isActive
                                ? "bg-indigo-600 text-white shadow-sm"
                                : "text-slate-700 hover:bg-slate-100",
                            )}
                          >
                            <div
                              className={cn(
                                "h-7 w-7 rounded-md flex items-center justify-center shrink-0",
                                isActive
                                  ? "bg-white/20"
                                  : isFullAccess
                                    ? "bg-rose-50 text-rose-500"
                                    : "bg-indigo-50 text-indigo-500",
                              )}
                            >
                              <ShieldCheck className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div
                                className={cn(
                                  "font-semibold truncate leading-tight",
                                  isActive ? "text-white" : "text-slate-800",
                                )}
                              >
                                {r.name}
                              </div>
                              <div
                                className={cn(
                                  "text-[10px] truncate",
                                  isActive
                                    ? "text-indigo-200"
                                    : "text-slate-400",
                                )}
                              >
                                {isFullAccess
                                  ? "Full Access"
                                  : `${rFlatPerms.length || 0} quyền`}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                </div>
              </ScrollArea>

              {/* Role info panel at bottom */}
              {isEditing && (
                <div className="border-t border-slate-200 p-3 space-y-3 bg-white">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase font-bold text-slate-400">
                      Tên vai trò
                    </Label>
                    <Input
                      className="h-7 text-xs"
                      value={role.name}
                      onChange={(e) =>
                        setRole({ ...role, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase font-bold text-slate-400">
                      Mô tả
                    </Label>
                    <Textarea
                      className="text-xs min-h-[60px] resize-none"
                      value={role.description}
                      onChange={(e) =>
                        setRole({ ...role, description: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-md bg-rose-50 border border-rose-100">
                    <div>
                      <p className="text-[11px] font-bold text-rose-700">
                        Full Access
                      </p>
                      <p className="text-[9px] text-rose-400">
                        Toàn quyền hệ thống
                      </p>
                    </div>
                    <Switch
                      checked={roleFlatPerms.includes("*")}
                      onCheckedChange={handleFullAccessToggle}
                      className="data-[state=checked]:bg-rose-500 scale-90"
                    />
                  </div>
                </div>
              )}
            </aside>

            {/* ── Right: Permission Matrix ── */}
            <div className="flex-1 overflow-auto bg-white">
              {/* Mobile: role info & full access toggle */}
              <div className="md:hidden px-3 py-3 border-b border-slate-200 bg-slate-50 space-y-2">
                {isEditing && (
                  <div className="space-y-2">
                    <Input
                      className="h-8 text-xs"
                      value={role.name}
                      onChange={(e) =>
                        setRole({ ...role, name: e.target.value })
                      }
                      placeholder="Tên vai trò"
                    />
                    <div className="flex items-center justify-between p-2 rounded-md bg-rose-50 border border-rose-100">
                      <span className="text-[11px] font-bold text-rose-700">
                        Full Access
                      </span>
                      <Switch
                        checked={roleFlatPerms.includes("*")}
                        onCheckedChange={handleFullAccessToggle}
                        className="data-[state=checked]:bg-rose-500 scale-90"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Permission Table */}
              <div className="w-full">
                <table className="w-full min-w-[720px] text-xs">
                  {/* Sticky header */}
                  <thead className="sticky top-0 z-20 bg-slate-50 border-b border-slate-200 shadow-sm">
                    <tr>
                      <th className="text-left font-bold text-slate-500 py-2 px-3 w-[180px] lg:w-[220px] uppercase text-[10px] tracking-wider">
                        Module
                      </th>
                      {PERM_ACTIONS.map((action) => {
                        const Icon = action.icon;
                        return (
                          <th
                            key={action.id}
                            className="text-center font-bold text-slate-500 py-2 px-1 w-[56px] lg:w-[68px]"
                          >
                            <div className="flex flex-col items-center gap-0.5">
                              <Icon className={cn("h-3 w-3", action.color)} />
                              <span className="text-[9px] uppercase tracking-tight">
                                {action.label}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULE_GROUPS.map((group) => {
                      const GroupIcon = group.icon;
                      return (
                        <Fragment key={group.group}>
                          {/* Group header row */}
                          <tr className="bg-slate-50 border-y border-slate-200 sticky top-[33px] z-10 shadow-sm">
                            <td
                              colSpan={PERM_ACTIONS.length + 1}
                              className="py-1.5 px-3"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={cn(
                                    "h-5 w-5 rounded flex items-center justify-center text-white",
                                    group.color,
                                  )}
                                >
                                  <GroupIcon className="h-3 w-3" />
                                </div>
                                <span className="font-bold text-[11px] text-slate-700 uppercase tracking-wider">
                                  {group.group}
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* Module rows */}
                          {group.modules.map((mod) => (
                            <tr
                              key={mod.id}
                              className="border-b border-slate-100 hover:bg-indigo-50/20 transition-colors"
                            >
                              <td className="py-1.5 px-3 pl-10">
                                <span className="font-medium text-slate-700">
                                  {mod.name}
                                </span>
                              </td>
                              {PERM_ACTIONS.map((action) => {
                                const checked = hasPerm(mod.id, action.id);
                                const isAll = action.id === "all";
                                const disabled =
                                  !isEditing || roleFlatPerms.includes("*");

                                return (
                                  <td
                                    key={action.id}
                                    className="text-center py-1.5 px-1"
                                  >
                                    <div className="flex justify-center">
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={() =>
                                          togglePerm(mod.id, action.id)
                                        }
                                        disabled={disabled}
                                        className={cn(
                                          "h-4 w-4 rounded-[3px] border-slate-300 transition-colors",
                                          checked &&
                                            !disabled &&
                                            isAll &&
                                            "data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600",
                                          checked &&
                                            !disabled &&
                                            !isAll &&
                                            "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600",
                                          disabled &&
                                            checked &&
                                            "data-[state=checked]:bg-slate-400 data-[state=checked]:border-slate-400 opacity-60",
                                        )}
                                      />
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Alert */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Xác nhận xóa vai trò</AlertDialogTitle>
              <AlertDialogDescription>
                Hành động này không thể hoàn tác. Vai trò "{role.name}" sẽ bị
                xóa vĩnh viễn.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Hủy</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-rose-600 hover:bg-rose-700"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}{" "}
                Xóa
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </StandardDetailLayout>
    </>
  );
}
